"use client";

import "./stories-carousel.css";

import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useState, type CSSProperties } from "react";

import type { StoryChapter } from "@/lib/stories";
import { cn } from "@/lib/utils";

import StoryLightbox from "./story-lightbox";
import { youtubeEmbedSrc } from "./youtube-embed";

export interface DeckStory {
  slug: string;
  title: string;
  name: string;
  role: string;
  location: string;
  heroTagline: string;
  poster: string;
  posterAlt: string;
  youtubeUrl?: string;
  videoDuration?: string;
  chapters?: StoryChapter[];
}

interface StoriesDeckProps {
  stories: DeckStory[];
}

/** Neighbours sit just under the focus card rather than being thumbnails. */
const FLANK_SCALE = 0.88;

/** How far the focus card laps each neighbour, as a fraction of its own width. */
const OVERLAP = 0.05;

/**
 * Distance between slide centres, as a fraction of the focus card's width.
 *
 * Derived rather than eyeballed so it survives a change to FLANK_SCALE: half a
 * scaled neighbour plus half the focus card, less the overlap.
 */
const STEP_RATIO = FLANK_SCALE / 2 + 0.5 - OVERLAP;

/** Dwell per slide before the deck advances itself. */
const AUTO_ADVANCE_MS = 6000;

/**
 * Auto-advance is motion the visitor did not ask for, so it is the one piece
 * here that needs an explicit reduced-motion check: the global CSS block only
 * neutralises transitions, and deliberately leaves `animation` alone.
 */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    // jsdom has no matchMedia, and neither do a few older embedded browsers.
    // Absent the API, assume full motion rather than crashing the deck.
    if (typeof window.matchMedia !== "function") {
      return;
    }
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(query.matches);
    const onChange = () => {
      setReduced(query.matches);
    };
    query.addEventListener("change", onChange);
    return () => {
      query.removeEventListener("change", onChange);
    };
  }, []);

  return reduced;
}

function Chevron({ direction }: { direction: "prev" | "next" }) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden fill="none">
      <path
        d={direction === "prev" ? "M11 4L6 9l5 5" : "M7 4l5 5-5 5"}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Coverflow index. All three visible cards are laid out from one origin and
 * moved with `transform` alone, so stepping is a single compositor-driven slide
 * rather than a swap of which slot holds which poster.
 *
 * The global reduced-motion block strips transform transitions, so this
 * degrades to an instant state change without a media query here.
 */
export default function StoriesDeck({ stories }: StoriesDeckProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [held, setHeld] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  const total = stories.length;
  const step = useCallback(
    (delta: number) => {
      setActiveIndex((current) => (current + delta + total) % total);
    },
    [total],
  );

  // Hold while the visitor is reading a card, driving it by hand, or watching a
  // clip. Advancing under any of those is the carousel talking over them.
  const advancing =
    total > 1 && !held && playingIndex === null && !prefersReducedMotion;

  useEffect(() => {
    if (!advancing) {
      return;
    }
    const timer = setTimeout(() => {
      step(1);
    }, AUTO_ADVANCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [advancing, activeIndex, step]);

  if (total === 0) {
    return null;
  }

  const active = stories[activeIndex]!;
  const playing = playingIndex === null ? null : stories[playingIndex];
  const playingClip = youtubeEmbedSrc(playing?.youtubeUrl);
  const upNext = stories[(activeIndex + 1) % total]!;

  /**
   * Seat each story relative to the focused one, taking the shorter way round
   * so a step moves one seat rather than the long way across the deck.
   */
  const seatOf = (index: number) => {
    let seat = index - activeIndex;
    if (seat > total / 2) {
      seat -= total;
    }
    if (seat < -total / 2) {
      seat += total;
    }
    return seat;
  };

  return (
    <section
      aria-roledescription="carousel"
      aria-label="Expert story highlights"
      onMouseEnter={() => {
        setHeld(true);
      }}
      onMouseLeave={() => {
        setHeld(false);
      }}
      onFocusCapture={() => {
        setHeld(true);
      }}
      onBlurCapture={() => {
        setHeld(false);
      }}
    >
      <div
        style={
          { "--step": `calc(var(--card-w) * ${STEP_RATIO})` } as CSSProperties
        }
        /*
          `stories-deck-stage` owns --card-w. The stage is the site container's
          content column, so the deck is bounded by the same 1216 as every other
          block on the site; the flanks peek inside it and the gradients fade them
          out at its edges.

          clip-path rather than overflow-hidden: the off-screen slides still have
          to be cut off horizontally, but overflow also cropped the focus card's
          drop shadow flat against the top and bottom edges. Negative insets
          leave the shadow room to fall outside the box.
        */
        className="stories-deck-stage relative [clip-path:inset(-140px_0px)]"
      >
        {/*
          In-flow spacer: gives the stage exactly the focus card's height at
          any width, so the deck cannot be shorter than the card it holds. The
          three fixed heights it replaces were already 18px short at 768.
        */}
        <div aria-hidden className="mx-auto aspect-video w-[var(--card-w)]" />
        {/*
          Keyed by slug alone. Keying by seat as well meant every key changed on
          a step, so React remounted all three cards and the browser had no
          previous transform to animate from: the slide silently never ran.
        */}
        {stories.map((story, index) => {
          const seat = seatOf(index);
          const isActive = seat === 0;
          const offDeck = Math.abs(seat) > 1;
          return (
            <div
              key={story.slug}
              aria-hidden={!isActive}
              style={{
                transform: `translate(calc(-50% + ${seat} * var(--step)), -50%) scale(${
                  isActive ? 1 : FLANK_SCALE
                })`,
                zIndex: isActive ? 5 : 1,
              }}
              className={cn(
                "absolute top-1/2 left-1/2 aspect-video w-[var(--card-w)] transition-[transform,opacity] duration-[550ms] ease-out",
                isActive && "opacity-100",
                !isActive && "pointer-events-none opacity-0 lg:opacity-45",
                offDeck && "opacity-0 lg:opacity-0",
              )}
            >
              <DeckCard
                story={story}
                isActive={isActive}
                onPlay={() => {
                  setPlayingIndex(index);
                }}
              />
            </div>
          );
        })}

        {total > 1 && (
          <>
            {/* Narrower than the 260px this was when the stage ran to the
                viewport edge: inside the 1216 column a neighbour only shows
                ~190px, and a 260px fade swallowed all of it. Enough to soften
                where the flank is clipped, not enough to erase it. */}
            <div
              aria-hidden
              className="from-background pointer-events-none absolute inset-y-0 left-0 z-[3] hidden w-[120px] bg-gradient-to-r to-transparent lg:block"
            />
            <div
              aria-hidden
              className="from-background pointer-events-none absolute inset-y-0 right-0 z-[3] hidden w-[120px] bg-gradient-to-l to-transparent lg:block"
            />
          </>
        )}

        {total > 1 && (
          <>
            <button
              type="button"
              onClick={() => {
                step(-1);
              }}
              aria-label="Previous story"
              className="border-marketing-border-light text-foreground bg-background stories-deck-prev absolute top-1/2 left-1 z-10 flex size-10 -translate-y-1/2 items-center justify-center rounded-full border shadow-[0_4px_14px_-2px_rgba(0,0,0,0.08)] transition-colors hover:bg-neutral-50 sm:left-3 sm:size-13"
            >
              <Chevron direction="prev" />
            </button>
            <button
              type="button"
              onClick={() => {
                step(1);
              }}
              aria-label="Next story"
              className="border-marketing-border-light text-foreground bg-background stories-deck-next absolute top-1/2 right-1 z-10 flex size-10 -translate-y-1/2 items-center justify-center rounded-full border shadow-[0_4px_14px_-2px_rgba(0,0,0,0.08)] transition-colors hover:bg-neutral-50 sm:right-3 sm:size-13"
            >
              <Chevron direction="next" />
            </button>
          </>
        )}
      </div>

      <div className="mt-5 flex flex-col items-center gap-2 px-6 text-center sm:mt-6 sm:gap-2.5">
        <h2 className="text-foreground text-[1.375rem]! leading-tight! font-medium! tracking-tight! sm:text-[1.75rem]!">
          {active.title}
        </h2>
        <p className="text-marketing-text-muted hidden max-w-[41rem] text-base leading-relaxed sm:block dark:text-neutral-400">
          {active.heroTagline}
        </p>
        <div className="mt-2.5 flex flex-wrap items-center justify-center gap-x-5 gap-y-3 sm:mt-3.5">
          {/* `/join`, not `/apply`: the apply flow is only entered at
              `/apply/start/<token>` for one opportunity, so the bare path is
              not a page. */}
          <Link
            href="/join"
            className="bg-marketing-blue inline-flex items-center rounded-full px-7 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90"
          >
            Join the panel
          </Link>
          <Link
            href={`/stories/${active.slug}`}
            className="text-marketing-blue group inline-flex items-center gap-2 text-sm font-medium"
          >
            Read the full story
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              aria-hidden
              fill="none"
              className="transition-transform group-hover:translate-x-1"
            >
              <path
                d="M3 8h9M8 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        </div>
      </div>

      {total > 1 && (
        <div className="mt-5 flex items-center justify-center gap-2 sm:mt-6">
          {stories.map((story, index) => {
            const isActive = index === activeIndex;
            return (
              <button
                key={story.slug}
                type="button"
                onClick={() => {
                  setActiveIndex(index);
                }}
                aria-label={`Show ${story.title}`}
                aria-current={isActive}
                className="group w-10 py-2"
              >
                <span className="block h-[3px] w-full overflow-hidden rounded-full bg-neutral-300 group-hover:bg-neutral-400">
                  {/*
                    Remounted on every step (the key carries activeIndex) so the
                    keyframe restarts from zero rather than resuming.
                  */}
                  <span
                    key={`${story.slug}-${active.slug}`}
                    className={cn(
                      "stories-deck-progress bg-marketing-blue block h-full origin-left rounded-full",
                      isActive ? "scale-x-100" : "scale-x-0",
                    )}
                    style={
                      isActive && advancing
                        ? {
                            animation: `stories-deck-progress ${AUTO_ADVANCE_MS}ms linear forwards`,
                          }
                        : undefined
                    }
                  />
                </span>
              </button>
            );
          })}
        </div>
      )}

      {playing && playingClip ? (
        <StoryLightbox
          story={{
            slug: playing.slug,
            title: playing.title,
            name: playing.name,
            role: playing.role,
            location: playing.location,
            poster: playing.poster,
            posterAlt: playing.posterAlt,
            src: playingClip,
            videoDuration: playing.videoDuration,
            chapters: playing.chapters,
          }}
          upNextTitle={total > 1 ? upNext.title : undefined}
          onClose={() => {
            setPlayingIndex(null);
          }}
        />
      ) : null}
    </section>
  );
}

function DeckCard({
  story,
  isActive,
  onPlay,
}: {
  story: DeckStory;
  isActive: boolean;
  onPlay: () => void;
}) {
  const clip = youtubeEmbedSrc(story.youtubeUrl);

  const art = (
    <>
      {/* A filmed story's cover is the clip's own poster frame, so the card
          previews the film it opens rather than a separate still. */}
      <Image
        src={story.poster}
        alt={isActive ? story.posterAlt : ""}
        fill
        sizes="(max-width: 1024px) 100vw, 840px"
        priority={isActive}
        className={cn(
          "object-cover",
          // Heavier than seems necessary: several covers carry baked-in
          // headlines that stay legible at 3px and compete with the focused
          // card for attention.
          !isActive && "scale-105 blur-[8px]",
        )}
      />
      {isActive && (
        <>
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/60 to-transparent"
          />
          {/* Insets grow with the card: the focus card is 840px at lg, where a
              flat 20px read as stuck to the corner. */}
          {story.videoDuration && (
            <span className="absolute top-5 right-5 hidden rounded-full bg-black/45 px-3 py-1.5 font-mono text-xs text-white sm:inline-block lg:top-8 lg:right-8">
              {story.videoDuration}
            </span>
          )}
          <div className="absolute bottom-6 left-6 hidden text-left sm:bottom-8 sm:left-8 sm:block lg:bottom-10 lg:left-10">
            <p className="text-lg font-medium text-white sm:text-[1.375rem]">
              {story.name}
            </p>
            <p className="mt-1 text-sm text-white/80">
              {story.role} · {story.location}
            </p>
          </div>
          {clip ? (
            <span
              aria-hidden
              className="absolute top-1/2 left-1/2 flex size-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 sm:size-24"
            >
              <svg
                viewBox="0 0 30 34"
                aria-hidden
                className="ml-1 size-5 sm:size-7"
                fill="currentColor"
              >
                <path
                  d="M4 3 L26 17 L4 31 Z"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          ) : (
            <span className="absolute inset-x-0 bottom-0 flex justify-end p-5 sm:p-8 lg:p-10">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-[0.8125rem] font-medium text-neutral-900">
                Read the story
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  aria-hidden
                  fill="none"
                >
                  <path
                    d="M3 8h9M8 4l4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
          )}
        </>
      )}
    </>
  );

  const shell =
    "relative block size-full overflow-hidden rounded-2xl bg-neutral-200 shadow-[0_26px_64px_-14px_rgba(0,0,0,0.22)] transition-transform duration-200 ease-out";

  // Flanks are scenery: not focusable, not clickable, no accessible name.
  if (!isActive) {
    return <div className={shell}>{art}</div>;
  }

  if (!clip) {
    return (
      <Link
        href={`/stories/${story.slug}`}
        aria-label={`Read ${story.title}`}
        className={cn(shell, "active:scale-[0.995]")}
      >
        {art}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onPlay}
      aria-label={`Play ${story.title}`}
      className={cn(shell, "active:scale-[0.995]")}
    >
      {art}
    </button>
  );
}
